import React, { useMemo, useState, useEffect, useCallback } from 'react';
import Drawer from '../../../../../components/src/UI/Drawer';
import BallGrid from '../BallGrid';
import BallTypeTab from '../BallTypeTab';
import QuickPickButtons from '../QuickPickButtons';
import { BALL_COLORS } from '../../constants';
import CloseButton from '../../../../../components/src/UI/CloseButton';
import classes from './BallEditDrawer.module.scss';

const BallEditDrawer = React.memo(({
  open,
  onClose,
  onConfirm,
  selectedNumbers,
  mainGridTotal,
  secondaryGridTotal,
  secondaryUnique,
  minNormalCount,
  maxNormalCount,
  requiredBonusCount,
  ticket,
  onValidationError,
}) => {
  const [localNormalValues, setLocalNormalValues] = useState([]);
  const [localBonusValues, setLocalBonusValues] = useState([]);
  const hasBonus = requiredBonusCount > 0;
  const [activeTab, setActiveTab] = useState('NORMAL');

  useEffect(() => {
    if (!open) return;
    const normalVals = selectedNumbers
      .filter((n) => n.type === 'NORMAL')
      .map((n) => n.value)
      .filter((v) => !Number.isNaN(v));
    const bonusVals = selectedNumbers
      .filter((n) => n.type === 'BONUS')
      .map((n) => n.value)
      .filter((v) => !Number.isNaN(v));
    setLocalNormalValues(normalVals);
    setLocalBonusValues(bonusVals);
    setActiveTab(maxNormalCount > 0 ? 'NORMAL' : 'BONUS');
  }, [open, selectedNumbers, maxNormalCount]);

  const handleNormalToggle = (value) => {
    setLocalNormalValues((prev) => {
      if (prev.includes(value)) return prev.filter((v) => v !== value);
      if (prev.length >= maxNormalCount) return prev;
      return [...prev, value];
    });
  };

  const handleBonusToggle = (value) => {
    setLocalBonusValues((prev) => {
      if (prev.includes(value)) return prev.filter((v) => v !== value);
      if (prev.length >= requiredBonusCount) return prev;
      return [...prev, value];
    });
  };

  const validationError = useMemo(() => {
    if (
      localNormalValues.length < minNormalCount
      || localNormalValues.length > maxNormalCount
    ) {
      const missing = minNormalCount - localNormalValues.length;
      return `Choose at least ${missing} number${missing > 1 ? 's' : ''} to continue`;
    }
    if (
      requiredBonusCount > 0
      && localBonusValues.length !== requiredBonusCount
    ) {
      return `Choose exactly ${requiredBonusCount} bonus ball${requiredBonusCount > 1 ? 's' : ''}`;
    }
    return null;
  }, [localNormalValues, localBonusValues, minNormalCount, maxNormalCount, requiredBonusCount]);

  const isValid = validationError === null;
  const disabledBonusValues = secondaryUnique ? localNormalValues : [];

  const handleConfirm = () => {
    if (!isValid) return;
    const newNumbers = [
      ...localNormalValues.map((value) => ({ type: 'NORMAL', value })),
      ...localBonusValues.map((value) => ({ type: 'BONUS', value })),
    ];
    onConfirm(newNumbers);
    onClose();
  };

  const derivedEditedNumbers = useMemo(
    () => [
      ...localNormalValues.map((value) => ({ type: 'NORMAL', value })),
      ...localBonusValues.map((value) => ({ type: 'BONUS', value })),
    ],
    [localNormalValues, localBonusValues]
  );

  const handleQuickPickSet = useCallback(
    (numbers) => {
      if (hasBonus) {
        if (activeTab === 'NORMAL') {
          setLocalNormalValues(numbers.filter((n) => n.type === 'NORMAL').map((n) => n.value));
        } else {
          setLocalBonusValues(numbers.filter((n) => n.type === 'BONUS').map((n) => n.value));
        }
        return;
      }
      setLocalNormalValues(numbers.filter((n) => n.type === 'NORMAL').map((n) => n.value));
      setLocalBonusValues(numbers.filter((n) => n.type === 'BONUS').map((n) => n.value));
    },
    [hasBonus, activeTab]
  );

  return (
    <Drawer isOpen={open} onClose={onClose} position="bottom">
      <div className={classes.Puller} />
      <div className={classes.Header}>
        <h3 className={classes.HeaderTitle}>Select Numbers</h3>
        <CloseButton onClick={onClose} />
      </div>

      {ticket && (
        <div className={classes.QuickPickSection}>
          <QuickPickButtons
            ticket={ticket}
            editedNumbers={derivedEditedNumbers}
            setEditedNumbers={handleQuickPickSet}
            setValidationError={onValidationError ?? (() => {})}
            minNormalCount={minNormalCount}
            requiredBonusCount={requiredBonusCount}
            lotteryId={ticket.draw_id}
          />
        </div>
      )}

      {hasBonus && maxNormalCount > 0 && (
        <div className={classes.TabsRow}>
          <BallTypeTab
            title="Normal Balls"
            currentCount={localNormalValues.length}
            minCount={minNormalCount}
            maxCount={maxNormalCount}
            isActive={activeTab === 'NORMAL'}
            onClick={() => setActiveTab('NORMAL')}
          />
          <BallTypeTab
            title="Bonus Balls"
            currentCount={localBonusValues.length}
            minCount={requiredBonusCount}
            maxCount={requiredBonusCount}
            isActive={activeTab === 'BONUS'}
            onClick={() => setActiveTab('BONUS')}
          />
        </div>
      )}

      <div className={`${classes.Content}${hasBonus && maxNormalCount === 0 ? ` ${classes.ContentBonusOnly}` : ''}`}>
        {hasBonus ? (
          activeTab === 'NORMAL' ? (
            maxNormalCount > 0 && (
              <BallGrid
                title="Normal Balls"
                maxValue={mainGridTotal}
                selectedValues={localNormalValues}
                minCount={minNormalCount}
                maxCount={maxNormalCount}
                onToggle={handleNormalToggle}
                ballColor={BALL_COLORS.NORMAL}
                showHeader={false}
              />
            )
          ) : (
            <BallGrid
              title="Bonus Balls"
              maxValue={secondaryGridTotal}
              selectedValues={localBonusValues}
              minCount={requiredBonusCount}
              maxCount={requiredBonusCount}
              onToggle={handleBonusToggle}
              ballColor={BALL_COLORS.BONUS}
              disabledValues={disabledBonusValues}
              showHeader={false}
            />
          )
        ) : (
          maxNormalCount > 0 && (
            <BallGrid
              title="Normal Balls"
              maxValue={mainGridTotal}
              selectedValues={localNormalValues}
              minCount={minNormalCount}
              maxCount={maxNormalCount}
              onToggle={handleNormalToggle}
              ballColor={BALL_COLORS.NORMAL}
            />
          )
        )}
      </div>

      <div className={classes.Footer}>
        {validationError && <p className={classes.Error}>{validationError}</p>}
        <button
          type="button"
          onClick={handleConfirm}
          disabled={!isValid}
          className={classes.ConfirmButton}
        >
          Confirm Selection
        </button>
      </div>
    </Drawer>
  );
});

BallEditDrawer.displayName = 'BallEditDrawer';

export default BallEditDrawer;
